import {
  Box,
  Checkbox,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  DateRange,
  MultiInputDateTimeRangeField,
} from "@mui/x-date-pickers-pro";
import {
  addDays,
  getDate,
  getHours,
  setDate as setDateFns,
  setMonth,
  setYear,
} from "date-fns";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { useAppSelector } from "../../../../../app/hooks";
import { RootState } from "../../../../../app/store";
import InfoTooltip from "../../../../../common/components/functional/InfoTooltip/InfoTooltip";
import CommonTextField from "../../../../../common/components/functional/commonTextField/CommonTextField";
import i18n from "../../../../../i18n";
import { EditEntry, Supplement } from "../../../entriesAPI";
import {
  shouldEnforceNegativeValue,
  shouldShowSupplementDescription,
} from "../helpers";
import {
  getSupplementInputAllowance,
  initialSupplementInput,
  supplementInputHasError,
} from "../supplementInputTypes";

interface Props {
  originalEntry?: EditEntry | null;
  entry: EditEntry;
  updateSupplements?: (newSupplements: Supplement[]) => void;
  inputErrors: { [code: string]: boolean };
  setInputErrors: (inputErrors: { [code: string]: boolean }) => void;
  hideTitle?: boolean;
  isDisabled?: boolean;
  hideUnchecked?: boolean;
}

const Supplements = ({
  originalEntry,
  entry,
  updateSupplements,
  inputErrors,
  setInputErrors,
  hideTitle,
  isDisabled,
  hideUnchecked,
}: Props) => {
  const { t } = useTranslation();

  const { assignment, translatedSupplementNames, translatedUnitNames } =
    useAppSelector((state: RootState) => ({
      assignment: state.talents.assignment,
      translatedSupplementNames: state.localized.supplement,
      translatedUnitNames: state.localized.unit,
    }));

  // Effect to run validation whenever entry.start, entry.end or supplements changes
  useEffect(() => {
    const newErrors = { ...inputErrors };
    entry?.supplements.forEach((supplement) => {
      const definition = assignment?.supplements[supplement.code]?.definition;
      if (definition?.type !== "TIMERANGE") return;

      if (
        supplementInputHasError(supplement, definition, entry.start, entry.end)
      ) {
        newErrors[supplement.code] = true;
      } else {
        delete newErrors[supplement.code];
      }
    });
    setInputErrors && setInputErrors({ ...newErrors });
    // Prevent loop by ignoring inputErrors and setInputErrors
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.start, entry.end, entry?.supplements, assignment?.supplements]);

  // When checkbox is toggled, add or remove supplement from entry supplements
  const toggleSupplement = (
    key: string,
    checked: boolean,
    exclusivityGroup: number | null,
    groupType: string,
  ) => {
    let newSupplements = [];

    if (checked) {
      const confirmedKey =
        exclusivityGroup != null
          ? Object.entries(assignment.supplements).find(
              ([, supplement]) =>
                supplement?.definition?.exclusivityGroup === exclusivityGroup,
            )?.[0] || key
          : key;

      const definition = assignment.supplements[confirmedKey]?.definition;
      const supplementProps = {
        code: confirmedKey,
        ...initialSupplementInput(definition, entry),
        ...(groupType !== "ON_OFF" &&
        groupType !== "TIMERANGE" &&
        exclusivityGroup == null
          ? { amount: "" }
          : {}),
        ...(shouldShowSupplementDescription(confirmedKey)
          ? { description: "" }
          : {}),
      };

      // Add supplement
      newSupplements = [...entry.supplements, supplementProps];
    } else {
      newSupplements = removeSupplement(key, exclusivityGroup);
    }
    updateSupplements && updateSupplements(newSupplements);
  };

  const renderDescriptionField = (supplement: Supplement | undefined) => {
    if (!supplement || !shouldShowSupplementDescription(supplement.code)) {
      return null;
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          maxWidth: "300px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Typography variant="secondary">
            {t("hourEntries:supplementTravelDescription")}
          </Typography>
          <InfoTooltip
            title={t("hourEntries:supplementTravelDescriptionTooltip")}
          />
        </Box>
        <CommonTextField
          multiline
          minRows={2}
          maxRows={4}
          fullWidth
          disabled={isDisabled}
          value={supplement.description ?? ""}
          onChange={(e) => onDescriptionChange(e.target.value, supplement.code)}
          inputProps={{ maxLength: 1023 }}
        />
      </Box>
    );
  };

  const removeSupplement = (key: string, exclusivityGroup: number | null) => {
    if (exclusivityGroup != null) {
      const keysInGroup = Object.entries(assignment.supplements)
        .filter(
          ([_, supplement]) =>
            supplement?.definition?.exclusivityGroup === exclusivityGroup,
        )
        .map(([key, _]) => key);

      return entry.supplements.filter(
        (supplement: Supplement) => !keysInGroup.includes(supplement.code),
      );
    } else {
      return entry.supplements.filter(
        (supplement: Supplement) => supplement.code !== key,
      );
    }
  };

  const onChange = (value: string, key: string, allowNegative = false) => {
    const pattern = allowNegative ? /^-?\d+(\.\d+)?$/ : /^\d+(\.\d+)?$/;
    if (
      value === "" ||
      (allowNegative && value === "-") ||
      pattern.test(value)
    ) {
      const updatedSupplements = entry.supplements.map(
        (supplement: Supplement) => {
          if (supplement.code === key) {
            return {
              ...supplement,
              amount: value,
            };
          }
          return supplement;
        },
      );
      updateSupplements && updateSupplements(updatedSupplements);
    }
  };

  const onTimerangeAmountChange = (value: string, key: string) => {
    const definition = assignment.supplements[key]?.definition;
    const { allowsTimeRange } = getSupplementInputAllowance(definition);
    const updatedSupplements = entry.supplements.map(
      (supplement: Supplement) => {
        if (supplement.code === key) {
          return {
            ...supplement,
            amount: value || null,
            // Clear time when amount is entered so only one mode is active at a time
            time: value
              ? null
              : allowsTimeRange
                ? { start: entry.start, end: entry.end }
                : null,
          };
        }
        return supplement;
      },
    );
    updateSupplements && updateSupplements(updatedSupplements);
  };

  const updateSupplementTime = (updatedData: {
    start: number;
    end: number;
    id: string;
  }) => {
    const code = updatedData.id;
    // Create a copy of the supplements array
    const updatedSupplements = entry.supplements.map(
      (supplement: Supplement) => {
        if (supplement.code === code) {
          const newTime = { start: updatedData.start, end: updatedData.end };
          return {
            ...supplement,
            time: newTime,
            amount: null,
          };
        }
        return supplement;
      },
    );

    // Remove the input error from the array if it exists
    let newErrors = inputErrors;
    if (newErrors[updatedData.id]) {
      delete newErrors[updatedData.id];
      setInputErrors && setInputErrors({ ...newErrors });
    }

    updateSupplements && updateSupplements(updatedSupplements);
  };

  const onDescriptionChange = (value: string, key: string) => {
    const updatedSupplements = entry.supplements.map(
      (supplement: Supplement) => {
        if (supplement.code === key) {
          return {
            ...supplement,
            description: value,
          };
        }

        return supplement;
      },
    );

    updateSupplements && updateSupplements(updatedSupplements);
  };

  const updateTime = (newValue: DateRange<Date> | null, id: string) => {
    if (
      !newValue ||
      !newValue[0] ||
      !newValue[1] ||
      isNaN(newValue[0].getTime()) ||
      isNaN(newValue[1].getTime())
    ) {
      updateSupplementTime({ start: entry.start, end: entry.end, id });
      return;
    }

    let startDate = newValue[0];
    let endDate = newValue[1];

    // Set dates using entry times as reference
    const entryStart = new Date(entry.start);
    const entryEnd = new Date(entry.end);
    const entryStartDay = getDate(entryStart);
    const entryEndDay = getDate(entryEnd);

    const startHour = getHours(startDate);
    const endHour = getHours(endDate);
    const entryStartHour = getHours(entryStart);
    const entryEndHour = getHours(entryEnd);

    // Set start and end dates based on entry times
    if (entryStartDay !== entryEndDay) {
      // Night shift: both start and end can be on different days

      // Set start date
      if (startHour >= entryStartHour) {
        // Start belongs to entry start date (evening/night)
        startDate = setYear(startDate, entryStart.getFullYear());
        startDate = setMonth(startDate, entryStart.getMonth());
        startDate = setDateFns(startDate, entryStartDay);
      } else {
        // Start belongs to entry end date (early morning)
        startDate = setYear(startDate, entryEnd.getFullYear());
        startDate = setMonth(startDate, entryEnd.getMonth());
        startDate = setDateFns(startDate, entryEndDay);
      }

      // Set end date
      if (endHour <= entryEndHour) {
        // End belongs to entry end date (early morning)
        endDate = setYear(endDate, entryEnd.getFullYear());
        endDate = setMonth(endDate, entryEnd.getMonth());
        endDate = setDateFns(endDate, entryEndDay);
      } else {
        // End belongs to entry start date (evening/night)
        endDate = setYear(endDate, entryStart.getFullYear());
        endDate = setMonth(endDate, entryStart.getMonth());
        endDate = setDateFns(endDate, entryStartDay);
      }
    } else {
      // Day shift: both start and end are always on same day
      startDate = setYear(startDate, entryStart.getFullYear());
      startDate = setMonth(startDate, entryStart.getMonth());
      startDate = setDateFns(startDate, entryStartDay);

      endDate = setYear(endDate, entryStart.getFullYear());
      endDate = setMonth(endDate, entryStart.getMonth());
      endDate = setDateFns(endDate, entryStartDay);
    }

    // If end is before start, move end to next day (only for night shifts)
    if (
      endDate.getTime() <= startDate.getTime() &&
      entryStartDay !== entryEndDay
    ) {
      endDate = addDays(endDate, 1);
    }

    const newStart = startDate.getTime();
    const newEnd = endDate.getTime();

    // Check if the resulting dates are valid
    if (!isNaN(newStart) && !isNaN(newEnd) && newEnd > newStart) {
      updateSupplementTime({
        start: newStart,
        end: newEnd,
        id: id,
      });

      // Remove the input error from the array if it exists
      let errors = { ...inputErrors };
      delete errors[id];
      setInputErrors(errors);
    } else {
      // Handle the case where the resulting dates are invalid
      let errors = { ...inputErrors };
      errors[id] = true;
      setInputErrors(errors);
    }
  };

  const handleSupplementOnChange = (value: string, keysInGroup: string[]) => {
    const definition = assignment.supplements[value]?.definition;

    // Update existing supplements and remove those with the same exclusivityGroup
    const updatedSupplements = entry.supplements
      .filter(
        (supplement: Supplement) => !keysInGroup.includes(supplement.code),
      )
      .concat({
        code: value,
        ...initialSupplementInput(definition, entry),
        ...(shouldShowSupplementDescription(value) ? { description: "" } : {}),
      }); // Add the new supplement

    // Run update supplements to actually add them to entry supplements
    updateSupplements && updateSupplements(updatedSupplements);
  };

  const renderTimerangeInput = (code: string) => {
    const currentSupplement = entry.supplements.find(
      (supplement) => supplement.code === code,
    );
    const { allowsValue, allowsTimeRange } = getSupplementInputAllowance(
      assignment.supplements[code]?.definition,
    );
    const hasAmount = allowsValue && !!currentSupplement?.amount;

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          maxWidth: "300px",
        }}
      >
        {allowsTimeRange && (
          <MultiInputDateTimeRangeField
            format={"HH:mm"}
            value={[
              currentSupplement?.time
                ? new Date(currentSupplement.time.start)
                : null,
              currentSupplement?.time
                ? new Date(currentSupplement.time.end)
                : null,
            ]}
            onChange={(newValue) => updateTime(newValue, code)}
            disabled={hasAmount}
            paddingLeft={"0px"}
            slotProps={{
              textField: {
                size: "small",
                error: inputErrors[code] && !hasAmount,
                inputProps: {
                  style: {
                    padding: "5px 10px",
                  },
                },
              },
            }}
          />
        )}
        {allowsValue && (
          <NumericFormat
            value={currentSupplement?.amount ?? ""}
            onValueChange={(values) =>
              onTimerangeAmountChange(values.value, code)
            }
            decimalSeparator=","
            allowNegative={false}
            decimalScale={2}
            isAllowed={(values) =>
              !values.floatValue || values.floatValue <= 999.99
            }
            customInput={TextField}
            size="small"
            placeholder={t("hourEntries:supplementHoursAmount")}
            error={inputErrors[code] && (hasAmount || !allowsTimeRange)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {t("common:hourUnit")}
                </InputAdornment>
              ),
            }}
            inputProps={{
              style: { padding: "5px 10px" },
            }}
            sx={{ width: "128px" }}
          />
        )}
        {renderDescriptionField(currentSupplement)}
      </Box>
    );
  };

  const renderInput = (
    key: string,
    exclusivityGroup: number | null,
    groupType: string,
  ) => {
    const supplementType = exclusivityGroup != null ? "SELECT" : groupType;

    switch (supplementType) {
      case "AMOUNT":
      case "TRAVEL_DISTANCE":
      case "FIXED_SUM": {
        const currentSupplement = entry.supplements.find(
          (supplement: Supplement) => supplement.code === key,
        );
        const enforceNegativeValue = shouldEnforceNegativeValue(
          currentSupplement?.code,
        );

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              maxWidth: "300px",
            }}
          >
            <NumericFormat
              size="small"
              id="outlined-end-adornment"
              value={currentSupplement?.amount || ""}
              onValueChange={(values) =>
                onChange(values.value, key, enforceNegativeValue)
              }
              decimalSeparator=","
              allowNegative={enforceNegativeValue}
              decimalScale={2}
              customInput={TextField}
              error={inputErrors[key]}
              helperText={
                inputErrors[key] && enforceNegativeValue
                  ? t("hourEntries:valueNeedsToBeNegative")
                  : ""
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {
                      translatedUnitNames[
                        assignment.supplements[key]?.definition?.unitCode || ""
                      ]
                    }
                  </InputAdornment>
                ),
              }}
              inputProps={{
                style: { padding: "5px 10px" },
              }}
            />
            {renderDescriptionField(currentSupplement)}
          </Box>
        );
      }
      case "TIMERANGE": {
        return renderTimerangeInput(key);
      }
      case "SELECT": {
        // Possible keys in group
        const keysInGroup = Object.entries(assignment.supplements)
          .filter(
            ([_, supplement]) =>
              supplement?.definition?.exclusivityGroup === exclusivityGroup,
          )
          .map(([key, _]) => key);

        // Get the selected supplement from entry supplements
        const selectedSupplement =
          keysInGroup.length > 0
            ? Object.values(entry.supplements).find((supplement: Supplement) =>
                keysInGroup.includes(supplement.code),
              )
            : null;

        // Filter supplements based on the prefix and exclusivityGroup
        const supplementOptions = Object.keys(assignment.supplements)
          .filter(
            (supplementKey) =>
              supplementKey.startsWith(key) &&
              assignment.supplements[supplementKey]?.definition
                ?.exclusivityGroup === exclusivityGroup,
          )
          .map((supplementKey) => {
            return supplementKey;
          });

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Select
                size="small"
                fullWidth
                displayEmpty
                value={selectedSupplement?.code || ""}
                onChange={(e) =>
                  e.target.value &&
                  handleSupplementOnChange(e.target.value, keysInGroup)
                }
                sx={{
                  backgroundColor: "rgba(249, 249, 250, 0.6)",
                }}
                renderValue={(supplement) =>
                  supplement
                    ? translatedSupplementNames[supplement] || supplement
                    : t("common:select...")
                }
              >
                {supplementOptions.map((supplement) => {
                  return (
                    <MenuItem key={supplement} value={supplement}>
                      {translatedSupplementNames[supplement] || supplement}
                    </MenuItem>
                  );
                })}
              </Select>
            </Box>
            {groupType === "TIMERANGE" &&
              selectedSupplement &&
              renderTimerangeInput(selectedSupplement.code)}
          </Box>
        );
      }
      default: {
        const currentSupplement = entry.supplements.find(
          (supplement) => supplement.code === key,
        );
        return renderDescriptionField(currentSupplement); // Default case: ON_OFF is just checkbox
      }
    }
  };

  // Create an array of supplement keys and translated names
  const supplementArray =
    assignment &&
    Object.entries(assignment.supplements)
      .reduce<
        {
          key: string;
          name: string;
          exclusivityGroup: number | null;
          type: string;
          isAlwaysOn: boolean;
        }[]
      >((acc, [key, supplement]) => {
        const exclusivityGroup =
          supplement.definition?.exclusivityGroup ?? null;

        // If supplement has mode FORCE and its key is found in entry supplements, it's always on
        const isAlwaysOn =
          supplement.mode === "FORCE" &&
          originalEntry &&
          originalEntry.supplements.some(
            (entrySupplement) => entrySupplement.code === key,
          )
            ? true
            : false;

        // Check if the current supplement belongs to an exclusivity group
        if (exclusivityGroup != null) {
          // Find all supplements with the same exclusivityGroup
          const supplementsWithSameGroup = Object.entries(
            assignment.supplements,
          ).filter(
            ([_, supplement]) =>
              supplement.definition?.exclusivityGroup === exclusivityGroup,
          );

          // Find the common prefix of supplements with the same group
          const commonPrefix = supplementsWithSameGroup.reduce(
            (prefix, [k, _]) => {
              const commonLength = Math.min(prefix.length, k.length);
              let i = 0;
              while (i < commonLength && prefix[i] === k[i]) {
                i++;
              }
              return prefix.slice(0, i);
            },
            supplementsWithSameGroup[0][0],
          );

          const translatedNames = supplementsWithSameGroup.map(([key, _]) => {
            return translatedSupplementNames[key] || key;
          });

          const commonName = translatedNames.reduce((prefix, name) => {
            const commonLength = Math.min(prefix.length, name.length);
            let i = 0;
            while (i < commonLength && prefix[i] === name[i]) {
              i++;
            }
            return prefix.slice(0, i);
          });

          // Check if the commonKey is already in the accumulator
          const existingEntry = acc.find((entry) => commonPrefix === entry.key);

          if (existingEntry) {
            // The commonKey is already in the accumulator, do nothing
          } else {
            // Add a new entry for unique commonPrefix
            acc.push({
              key: commonPrefix,
              // Use the first supplement's name to find prefix of translated names in group (Matkakorvaus <20 -> Matkakorvaus)
              // Notice: Doesn't work if supplement names in group dont have the same prefix
              // Will use common key prefix as name if no translation is found (matkakorvaus_)
              name: commonName || commonPrefix,
              exclusivityGroup,
              // All supplements in the same exclusivity group are expected to share the same type
              type: supplementsWithSameGroup[0][1].definition?.type ?? "ON_OFF",
              isAlwaysOn: isAlwaysOn,
            });
          }
        } else {
          // For supplements without exclusivityGroup, add them individually
          const key = Object.keys(assignment.supplements).find(
            (k) => assignment.supplements[k] === supplement,
          );
          acc.push({
            key: key || "",
            name: translatedSupplementNames[key || ""] || key || "",
            exclusivityGroup: null,
            type: supplement.definition?.type ?? "ON_OFF",
            isAlwaysOn: isAlwaysOn,
          });
        }

        return acc;
      }, [])
      .sort((a, b) => {
        return a.name.localeCompare(b.name, i18n.language);
      });

  return (
    <Grid item xs={6} sx={{ paddingRight: "15px" }}>
      <DialogTitle
        sx={{
          margin: "0px",
          padding: "0px",
        }}
      >
        {!hideTitle && (
          <Typography variant="header" sx={{ fontWeight: 600 }}>
            {t("hourEntries:supplements").toUpperCase()}
          </Typography>
        )}

        <Divider
          sx={{
            marginTop: "16px",
            marginBottom: "16px",
          }}
        />
      </DialogTitle>

      {entry.supplements &&
        assignment?.supplements &&
        supplementArray.map((item, index: number) => {
          // Check keys in group
          const keysInGroup = Object.entries(assignment.supplements)
            .filter(
              ([_, supplement]) =>
                supplement?.definition?.exclusivityGroup ===
                item.exclusivityGroup,
            )
            .map(([key, _]) => key);
          // See if supplement with matching code is in entry supplements
          const isChecked = entry.supplements.some(
            (supplement: Supplement) =>
              supplement.code === item.key ||
              keysInGroup.includes(supplement.code),
          );

          if (hideUnchecked === undefined || (hideUnchecked && isChecked)) {
            return (
              <Box
                key={index}
                sx={{
                  marginTop: "16px",
                  fontSize: "14px",
                  opacity: isDisabled ? 0.5 : 1,
                  pointerEvents: isDisabled ? "none" : "auto",
                }}
              >
                <Box sx={{ marginBottom: "5px" }}>
                  <Checkbox
                    checked={isChecked}
                    disabled={item.isAlwaysOn}
                    onChange={(e) =>
                      toggleSupplement(
                        item.key,
                        e.target.checked,
                        item.exclusivityGroup,
                        item.type,
                      )
                    }
                    sx={{
                      padding: 0,
                      marginRight: "8px",
                    }}
                  />
                  {item.name}
                </Box>
                {
                  // If checked, show inputfield
                  isChecked &&
                    renderInput(item.key, item.exclusivityGroup, item.type)
                }
              </Box>
            );
          } else {
            return null;
          }
        })}
    </Grid>
  );
};

export default Supplements;
